import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { NATIONAL_DEX, MAX_DEX, REGIONS } from '../data/nationalDex';
import DexSlot from './DexSlot';

const padDex = (num) => `#${String(num).padStart(3, '0')}`;

// Diacritic-insensitive fold so "flabebe" matches "Flabébé"
const fold = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const ALL_ENTRIES = Array.from({ length: MAX_DEX }, (_, i) => ({
  num: i + 1,
  name: NATIONAL_DEX[i + 1]
}));

const PokedexBinder = () => {
  const { pokedexBinder, getPokedexCompletion, removeFromPokedex } = useCollection();

  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('all');
  const [hintMsg, setHintMsg] = useState('');
  const hintTimer = useRef(null);

  useEffect(() => () => clearTimeout(hintTimer.current), []);

  const term = search.trim();
  const { filled, total } = getPokedexCompletion();
  const pct = total > 0 ? (filled / total) * 100 : 0;

  // Matching dex numbers for the current search term, in dex order
  const matches = useMemo(() => {
    if (!term) return null;
    const folded = fold(term);
    return ALL_ENTRIES.filter((e) => fold(e.name).includes(folded));
  }, [term]);

  const matchSet = useMemo(() => (matches ? new Set(matches.map((m) => m.num)) : null), [matches]);
  const firstMatch = matches?.[0];
  const firstMatchSlot = firstMatch ? pokedexBinder[firstMatch.num] : null;

  // Search bypasses the region filter — the full grid renders with
  // dim/highlight treatment so the user can locate the match visually.
  const visible = useMemo(() => {
    if (term) return ALL_ENTRIES;
    if (region === 'missing') return ALL_ENTRIES.filter((e) => !pokedexBinder[e.num]);
    const r = REGIONS.find((reg) => reg.key === region) || REGIONS[0];
    return ALL_ENTRIES.filter((e) => e.num >= r.from && e.num <= r.to);
  }, [term, region, pokedexBinder]);

  const showHint = (num, name) => {
    setHintMsg(`Open any ${name} card in your collection and tap "Add to Pokédex"`);
    clearTimeout(hintTimer.current);
    hintTimer.current = setTimeout(() => setHintMsg(''), 2500);
  };

  return (
    <div className="min-h-screen bg-cream-bg">
      {/* Navy sub-header (Sets/Series pattern: navy + 3px yellow border) */}
      <div className="bg-navy-dark border-b-[3px] border-yellow px-4 md:px-10 py-4 md:py-5">
        <div className="md:flex md:items-center md:justify-between">
          <div className="mb-3 md:mb-0">
            <div className="text-[11px] text-yellow font-bold uppercase tracking-[0.1em] mb-1">
              Your Binder
            </div>
            <h1 className="font-heading font-extrabold text-xl md:text-2xl text-white">
              Pokédex Collection
            </h1>
          </div>

          <div className="md:flex md:items-center md:gap-5">
            <div className="relative mb-3 md:mb-0">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search a Pokémon…"
                className="w-full md:w-[220px] bg-white/10 border border-white/[0.12] rounded-[10px] py-2 pl-9 pr-3 text-white text-[13px] placeholder-white/40 outline-none focus:border-yellow/60"
              />
            </div>

            {/* Mobile: progress + count in one row. Desktop: count then bar. */}
            <div className="flex items-center gap-3 md:gap-5">
              <div className="flex-1 md:flex-none md:order-2 md:w-40 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-yellow rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="md:order-1 text-right whitespace-nowrap">
                <span className="text-yellow text-sm md:text-xl font-bold">{filled} / {total}</span>
                <span className="hidden md:block text-[11px] text-white/40">slots filled</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Region filter pills — hidden on mobile while searching */}
      <div className={`${term ? 'hidden md:flex' : 'flex'} gap-2 bg-white border-b border-border px-4 md:px-10 py-3 overflow-x-auto`}>
        {REGIONS.map((r) => {
          const isActive = !term && region === r.key;
          return (
            <button
              key={r.key}
              onClick={() => setRegion(r.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-navy-dark text-white'
                  : 'bg-white border border-border-dark text-ink-secondary hover:border-navy-dark'
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Search result banner */}
      {term && (
        <div
          className={`mx-4 md:mx-10 mt-4 rounded-xl border px-4 py-3 flex items-center gap-2.5 text-[13px] font-semibold ${
            !firstMatch
              ? 'bg-cream-bg border-border text-ink-secondary'
              : firstMatchSlot
                ? 'bg-success-bg border-success-border text-success-green'
                : 'bg-danger-bg border-danger-border text-brick'
          }`}
        >
          {!firstMatch ? (
            <span className="font-normal">No Pokémon matching "{term}"</span>
          ) : (
            <>
              <span>{firstMatchSlot ? '✓' : '○'}</span>
              <span>
                {padDex(firstMatch.num)} {firstMatch.name} —{' '}
                {firstMatchSlot
                  ? `slot filled (${firstMatchSlot.cardName}, ${firstMatchSlot.setName})`
                  : 'slot empty — not yet added'}
              </span>
            </>
          )}
        </div>
      )}

      {/* Binder grid — strict National Dex order */}
      <div className="px-4 md:px-10 py-5 md:py-6 grid grid-cols-3 md:grid-cols-6 gap-2.5 md:gap-3.5">
        {visible.map(({ num, name }) => {
          const isMatch = matchSet?.has(num);
          const wrapperClass = term
            ? isMatch
              ? `rounded-xl shadow-[0_0_0_3px_#f5c518] transition-all ${matches.length === 1 ? 'scale-[1.04]' : ''}`
              : 'opacity-25 transition-all'
            : 'transition-all';
          return (
            <div key={num} className={wrapperClass}>
              <DexSlot
                num={num}
                name={name}
                entry={pokedexBinder[num]}
                onEmptyClick={showHint}
                onRemove={removeFromPokedex}
              />
            </div>
          );
        })}
      </div>

      {/* Empty-slot hint toast */}
      {hintMsg && (
        <div className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 z-50 bg-navy-dark text-white text-[13px] px-4 py-2.5 rounded-xl shadow-modal max-w-[90vw] text-center">
          {hintMsg}
        </div>
      )}
    </div>
  );
};

export default PokedexBinder;
